import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { logger } from 'src/analytics/KatalLogger';
import {
  EMBED_TO_PARENT_MESSAGES,
  PARENT_TO_EMBED_MESSAGES,
  EmbedToParentMessage,
  ParentToEmbedMessage
} from 'src/constants/embed-message-constants';
import { toggleTheme } from 'src/app/AppMetadataSlice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { useEmbedParams } from './useEmbedParams';
import { navigateWithEmbedParams, buildEmbedReportedPath, isSafeReportedPath } from 'src/utils/navigation-utils';
import { resolveFinSuiteParentOrigin, isFinSuiteOrigin } from 'src/utils/finsuite-origin';

/**
 * Hook that enables cross-frame communication between FinSuite (parent) and this embedded app.
 *
 * When the app is embedded in FinSuite's iframe:
 * 1. Announces readiness by sending FINSUITE_EMBED_READY to the parent window
 * 2. Listens for navigation commands from the parent (e.g., FINSUITE_NAVIGATE_HOME)
 * 3. Routes internally using React Router — no iframe remount needed
 *
 * When the app is NOT embedded, this hook is a no-op.
 *
 * To adopt this in another embedded app:
 * - Import and call this hook in your app's router component (inside BrowserRouter)
 * - Ensure your app has the embed-message-constants and navigateWithEmbedParams utility
 */
export const useParentMessageListener = (): void => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isEmbedded } = useEmbedParams();

  // Announce readiness to parent window
  useEffect(() => {
    if (!isEmbedded) return;

    try {
      const readyMessage: EmbedToParentMessage = { type: EMBED_TO_PARENT_MESSAGES.EMBED_READY };
      window.parent.postMessage(readyMessage, '*');
      logger.info('Sent FINSUITE_EMBED_READY to parent window');
    } catch (error) {
      logger.error('Failed to send embed ready message to parent', { error });
    }
  }, [isEmbedded]);

  // Report the current in-app path to the parent so it can deep-link its external
  // "open in new tab" link and reflect the path in its URL hash. Fires on mount and on
  // every route change. Embed plumbing params are stripped so they don't leak upstream.
  useEffect(() => {
    if (!isEmbedded) return;

    try {
      // Target the specific FinSuite parent origin rather than '*': the reported path can
      // include report IDs / query params, so broadcasting it to any embedding page is a
      // leak. Fail closed when the parent isn't a recognized FinSuite origin (or can't be
      // resolved, e.g. Firefox has no ancestorOrigins).
      const targetOrigin = resolveFinSuiteParentOrigin();
      if (!targetOrigin) {
        logger.debug('Skipping FINSUITE_CURRENT_PATH — parent is not a recognized FinSuite origin');
        return;
      }

      const path = buildEmbedReportedPath(location.pathname, location.search, location.hash);
      const message: EmbedToParentMessage = { type: EMBED_TO_PARENT_MESSAGES.CURRENT_PATH, payload: { path } };
      window.parent.postMessage(message, targetOrigin);
      // Debug-level: this fires once per navigation, so at info it would be a Katal log line
      // per click with no error signal. The catch below stays at error.
      logger.debug(`Sent FINSUITE_CURRENT_PATH to ${targetOrigin}: ${path}`);
    } catch (error) {
      logger.error('Failed to send current path to parent', { error });
    }
  }, [isEmbedded, location.pathname, location.search, location.hash]);

  // Listen for navigation commands from parent
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      // Ignore messages that aren't our protocol
      if (!event.data?.type) return;

      // Every parent→embed message below causes a side effect (navigation or theme change), so
      // verify the sender is a genuine FinSuite origin once here. Without this, any page that
      // merely frames xPT could post FINSUITE_NAVIGATE_HOME / _TO / _THEME_CHANGE and drive it.
      if (!isFinSuiteOrigin(event.origin)) {
        logger.warn(`Ignoring parent message from non-FinSuite origin: ${event.origin}`);
        return;
      }

      const message = event.data as ParentToEmbedMessage;

      switch (message.type) {
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME:
          logger.info('Received FINSUITE_NAVIGATE_HOME from parent, navigating to home');
          navigateWithEmbedParams(navigate, '/');
          break;
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_TO: {
          // Origin is already verified at the top of the handler. Validate the target path (the
          // parent derives it from a user-pasted URL hash) before routing.
          const targetPath = message.payload?.path;
          if (!isSafeReportedPath(targetPath)) {
            logger.warn(`Ignoring FINSUITE_NAVIGATE_TO with unsafe path: ${JSON.stringify(targetPath)}`);
            break;
          }
          logger.info(`Received FINSUITE_NAVIGATE_TO from parent, navigating to ${targetPath}`);
          navigateWithEmbedParams(navigate, targetPath);
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.THEME_CHANGE: {
          const isDarkMode = !!message.payload?.isDarkMode;
          logger.info(`Received FINSUITE_THEME_CHANGE from parent, switching to ${isDarkMode ? 'dark' : 'light'} mode`);
          dispatch(toggleTheme(isDarkMode));
          // Apply Cloudscape visual mode directly since XptTopNavigation (which normally calls applyMode)
          // is not rendered when embedded.
          applyMode(isDarkMode ? Mode.Dark : Mode.Light);
          break;
        }
        default:
          // Ignore unrecognized message types silently
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    logger.info('Parent message listener registered');

    return () => {
      window.removeEventListener('message', handleMessage);
      logger.info('Parent message listener unregistered');
    };
  }, [isEmbedded, navigate, dispatch]);
};
